import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@shared/ui/primitives/Button';
import { Banner } from '@shared/ui/feedback/Banner';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { useBrandingStore } from '@shared/stores/brandingStore';
import { toApiError } from '@shared/api/errors';
import { acceptPublicInvite, fetchPublicMaskedInvite } from './api';
import { SelfieUploadField } from './SelfieUploadField';

function statusPill(status: string): { label: string; className: string } {
  const raw = status.toLowerCase().replace(/_/g, ' ');
  const map: Record<string, string> = {
    draft: 'Draft',
    pending: 'Pending',
    accepted: 'Accepted',
    cancelled: 'Cancelled',
    expired: 'Expired',
  };
  const label = map[raw] || (raw ? raw.replace(/\b\w/g, (c) => c.toUpperCase()) : '—');
  const classes: Record<string, string> = {
    draft: 'bg-ink-muted/20 text-ink-secondary',
    pending: 'bg-amber-500/15 text-amber-800',
    accepted: 'bg-emerald-500/15 text-emerald-800',
    cancelled: 'bg-red-500/15 text-red-700',
    expired: 'bg-ink-muted/20 text-ink-muted',
  };
  return { label, className: `inline-flex rounded-full px-2.5 py-0.5 text-caption font-medium ${classes[raw] ?? 'bg-card hairline text-ink-secondary'}` };
}

function acceptButtonLabel(methods?: string[]): string {
  if (!methods?.length) return 'Accept & reveal PIN';
  const hasPin = methods.includes('pin') || methods.includes('otp');
  const hasQr = methods.includes('qr');
  if (hasQr && !hasPin) return 'Accept & reveal QR';
  if (hasPin && hasQr) return 'Accept & reveal PIN & QR';
  if (hasPin) return 'Accept & reveal PIN';
  return 'Accept invite';
}

/**
 * Public visitor accept flow — `/access/invite/:token` (no login).
 * Mirrors Angular `PublicAccessInviteAcceptComponent`.
 */
export function PublicAccessInviteAccept(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const branding = useBrandingStore((s) => s.branding);
  const [selfie, setSelfie] = useState<File | null>(null);
  const [selfieError, setSelfieError] = useState('');
  const [pin, setPin] = useState<string | null>(null);
  const [qrPayload, setQrPayload] = useState<string | null>(null);
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);
  const [actionError, setActionError] = useState('');

  const maskedQuery = useQuery({
    queryKey: ['public-access-invite', token],
    queryFn: () => fetchPublicMaskedInvite(token),
    enabled: !!token,
  });

  const acceptMutation = useMutation({
    mutationFn: () => acceptPublicInvite(token, { selfie: selfie ?? undefined }),
    onSuccess: (r) => {
      setAlreadyAccepted(!!r.already_accepted);
      setPin(r.pin ?? null);
      if (r.qr_payload) setQrPayload(r.qr_payload);
    },
  });

  const masked = maskedQuery.data;
  const status = String(masked?.status ?? '').toLowerCase();
  const cancelled = status === 'cancelled';
  const expired = status === 'expired';
  const requireSelfie = !!masked?.require_visitor_selfie;
  const locationLabel =
    typeof masked?.location_label === 'string' ? masked.location_label.trim() : '';
  const credentialMethods = masked?.credential_methods as string[] | undefined;

  const initialQr =
    typeof masked?.qr_payload === 'string' && masked.qr_payload.length > 0
      ? masked.qr_payload
      : null;
  const displayQr = qrPayload ?? initialQr;
  const showAccepted = alreadyAccepted || !!pin || !!displayQr;
  const wasAcceptedOnLoad =
    masked &&
    (status === 'accepted' || !!masked.accepted_at) &&
    !cancelled;

  const qrImageUrl = useMemo(() => {
    if (!displayQr) return '';
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(displayQr)}`;
  }, [displayQr]);

  const orgName = branding?.name ?? 'Community';
  const logoUrl = branding?.logoUrl ?? branding?.logoSmallUrl ?? '';
  const primary = branding?.primaryColor?.trim() || '#0f766e';

  if (!token) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <p className="text-body text-red-600">Invalid link.</p>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-dvh flex-col items-center justify-center bg-surface px-4 py-10"
      style={{ ['--access-primary' as string]: primary }}
    >
      <div className="w-full max-w-md rounded-2xl bg-card p-6 shadow-lg hairline">
        <div className="mb-6 text-center">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="mx-auto mb-3 h-12 w-auto object-contain" />
          ) : (
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary-600/10 font-display text-xl text-primary-700">
              {orgName.charAt(0)}
            </div>
          )}
          <h1 className="font-display text-display-sm text-ink">Access invite</h1>
          {orgName ? <p className="mt-1 text-body text-ink-secondary">{orgName}</p> : null}
        </div>

        {maskedQuery.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : maskedQuery.isError ? (
          <Banner variant="danger">{toApiError(maskedQuery.error).message}</Banner>
        ) : masked ? (
          <>
            <div className="mb-6 space-y-2 text-body text-ink">
              {masked.reference != null ? (
                <p>
                  <span className="font-medium">Reference:</span> {String(masked.reference)}
                </p>
              ) : null}
              {locationLabel ? (
                <p>
                  <span className="font-medium">Location:</span> {locationLabel}
                </p>
              ) : null}
              {status ? (
                <p className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">Status:</span>
                  <span className={statusPill(status).className}>{statusPill(status).label}</span>
                </p>
              ) : null}
              {masked.valid_until ? (
                <p>
                  <span className="font-medium">Valid until:</span>{' '}
                  {new Date(String(masked.valid_until)).toLocaleString()}
                </p>
              ) : null}
            </div>

            {cancelled ? (
              <Banner variant="danger">This invite is cancelled.</Banner>
            ) : showAccepted || wasAcceptedOnLoad ? (
              <div className="space-y-4">
                {wasAcceptedOnLoad && !pin && !displayQr ? (
                  <p className="text-body text-ink-secondary">This invite was already accepted.</p>
                ) : null}
                {pin ? (
                  <div className="rounded-xl bg-surface p-4 hairline">
                    <p className="mb-2 text-body text-ink-secondary">
                      Your access PIN (save this — shown once):
                    </p>
                    <code className="block text-center font-mono text-2xl tracking-widest text-ink">{pin}</code>
                  </div>
                ) : null}
                {displayQr ? (
                  <div className="rounded-xl bg-surface p-4 hairline">
                    <p className="mb-3 text-body text-ink-secondary">
                      Your access QR credential (save or screenshot):
                    </p>
                    {qrImageUrl ? (
                      <img
                        src={qrImageUrl}
                        alt="Access QR code"
                        width={220}
                        height={220}
                        className="mx-auto rounded-lg"
                      />
                    ) : null}
                    <code className="mt-3 block break-all text-caption text-ink-muted">{displayQr}</code>
                    <Button
                      type="button"
                      variant="secondary"
                      className="mt-3 w-full"
                      onClick={() => void navigator.clipboard.writeText(displayQr)}
                    >
                      Copy QR payload
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : expired ? (
              <Banner variant="danger">This invite has expired.</Banner>
            ) : (
              <div className="space-y-4">
                {actionError ? <Banner variant="danger">{actionError}</Banner> : null}
                {acceptMutation.isError ? (
                  <Banner variant="danger">{toApiError(acceptMutation.error).message}</Banner>
                ) : null}
                {requireSelfie ? (
                  <SelfieUploadField
                    value={selfie}
                    error={selfieError || undefined}
                    onChange={(file) => {
                      setSelfie(file);
                      if (file) setSelfieError('');
                    }}
                  />
                ) : null}
                <Button
                  type="button"
                  className="w-full"
                  disabled={acceptMutation.isPending}
                  onClick={() => {
                    if (requireSelfie && !selfie) {
                      setSelfieError('Please add a selfie before accepting.');
                      setActionError('');
                      return;
                    }
                    setActionError('');
                    acceptMutation.mutate();
                  }}
                >
                  {acceptMutation.isPending ? 'Processing…' : acceptButtonLabel(credentialMethods)}
                </Button>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
